import { LinearProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useProgressSample } from "../contexts/PlayerProgress.store";

// Don't run ahead of the last sample by more than this if updates stop
// (player disconnected, buffering, ...). Must exceed the desktop player's
// 5s heartbeat, or the bar stalls between reports.
const MAX_EXTRAPOLATION_SEC = 7;

interface PlayerProgressBarProps {
  duration?: number; // seconds
}

const PlayerProgressBar = ({ duration }: PlayerProgressBarProps) => {
  const sample = useProgressSample();
  const [progress, setProgress] = useState(sample.progress);

  useEffect(() => {
    setProgress(sample.progress);
    if (!sample.isPlaying) return;

    // Advance locally between samples so the bar moves every frame instead
    // of stepping once per server update.
    let frame = requestAnimationFrame(function tick(now) {
      const elapsed = Math.min(
        (now - sample.receivedAt) / 1000,
        MAX_EXTRAPOLATION_SEC
      );
      setProgress(sample.progress + Math.max(elapsed, 0));
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [sample]);

  const value = duration ? Math.min((progress / duration) * 100, 100) : 0;

  return (
    <LinearProgress
      variant="determinate"
      value={value}
      // MUI animates each value change over 0.4s; we already update per frame.
      sx={{ "& .MuiLinearProgress-bar": { transition: "none" } }}
    />
  );
};

export default PlayerProgressBar;
